import { AutosuggestProps } from '@amzn/awsui-components-console';
import debounce from 'debounce-promise';
import { Dispatch, useEffect, useState } from 'react';
import { useAuthState } from '../authentication/context';
import { callApi } from '../client';
import { NoTokenError } from '../errors/NoTokenError';
import { AutoSuggestStatus, Suggestions } from '../interfaces';

import {
  AutoSuggestPackageNameCommand,
  AutoSuggestPackageNameCommandOutput,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { NonCancelableEventHandler } from '@amzn/awsui-components-console/internal/events';
import { BaseChangeDetail } from '@amzn/awsui-components-console/input/interfaces';
import { Autobot } from '../Autobot';
import { PACKAGE_NAME_PATTERN } from '../ConferencePages/conferenceInfo/ui/PackagesEditor/packageValidation';

type Props = {
  ariaRequired?: boolean;
  disabled?: boolean;
  // Input label for accessibility
  ariaLabel?: string;

  // A package name to preload
  packageName?: string;

  // Callback function when a package name has been entered
  onChange?: NonCancelableEventHandler<BaseChangeDetail>;
};

async function callPackageSuggester(
  token: string,
  hint: string,
  setStatus?: Dispatch<AutoSuggestStatus>
): Promise<Suggestions> {
  if (!token) throw new NoTokenError();

  try {
    const response: AutoSuggestPackageNameCommandOutput = await callApi(
      new AutoSuggestPackageNameCommand({ hint }),
      token
    );

    if (response.$metadata.httpStatusCode == 200) {
      setStatus?.('finished');
      return response.suggestions;
    } else {
      console.error(
        'Unexpected response status code:',
        response.$metadata.httpStatusCode
      );
    }
  } catch (err: any) {
    setStatus?.('error');
    console.log('error with AutoSuggestPackageName API', err);
  }
}
const debouncedApi = debounce(callPackageSuggester, 1000);
const packageNameToOption = (name: string) => ({ value: name, label: name });
export const PackageSuggester = ({
  ariaRequired = false,
  disabled = false,
  ariaLabel,
  packageName,
  onChange,
}: Props) => {
  // Store last hint queried to avoid redundant queries
  const [lastHint, setLastHint] = useState('bananaboat9001');

  const { isAuthenticated, token } = useAuthState();
  const [availablePackagesStatus, setAvailablePackagesStatus] =
    useState<AutoSuggestStatus>('finished');
  const [availablePackagesList, setAvailablePackagesList] =
    useState<Suggestions>([]);
  const [availablePackagesOptions, setAvailablePackagesOptions] =
    useState<AutosuggestProps.Options>([]);

  const getAvailablePackages = async (): Promise<void> => {
    if (
      // Hint must be set and at least 3 characters
      !packageName ||
      packageName.length < 3
    )
      return setAvailablePackagesOptions([]);

    if (
      // Fetch must not be disabled
      availablePackagesStatus === 'error' ||
      // Must be authenticated or running on a dev desktop
      !isAuthenticated
    )
      return;
    try {
      if (!token) return;
      const availablePackages = await debouncedApi(
        token,
        packageName,
        setAvailablePackagesStatus
      );
      setLastHint(packageName);
      setAvailablePackagesList(availablePackages);
    } catch (err) {
      setAvailablePackagesStatus('error');
      console.log('error with FETCH', err);
    }
  };
  useEffect((): void => {
    if (availablePackagesList && availablePackagesList.length > 0) {
      const filteredPackagesOptions = availablePackagesList;
      const options: AutosuggestProps.Options =
        filteredPackagesOptions?.length > 0
          ? filteredPackagesOptions.sort().map(packageNameToOption)
          : [];
      setAvailablePackagesOptions(options);
    }
  }, [availablePackagesList]);

  return (
    <Autobot
      regex={PACKAGE_NAME_PATTERN}
      ariaRequired={ariaRequired}
      disabled={disabled}
      onKeyUp={() => getAvailablePackages()}
      onChange={onChange}
      value={packageName ?? ''}
      options={availablePackagesOptions}
      enteredTextLabel={(value) => `Use: "${value}"`}
      ariaLabel={ariaLabel}
      placeholder='Search for a package name'
      empty='No matches found'
      loadingText='Loading packages...'
      statusType={availablePackagesStatus}
      errorText={'Error retrieving packages.'}
      recoveryText={'Retry'}
    />
  );
};
